import { CardMediaPill } from '@app/components/base/CardMediaPill';
import { UploadedPlatformsStatus } from '@app/components/base/UploadedPlatformsStatus';
import type { webApi } from '@mi-front/api-clients';
import { Box } from '@mui/material';
import { getDisplayPlatformItems } from '../../../../list/fragment-card/base/FragmentCardStatuses';
import { ProjectCardMediaBlueprintGrid } from './ProjectCardMediaBlueprintGrid';
import { ProjectCardMediaEditorHoverOverlay } from './ProjectCardMediaEditorHoverOverlay';
import { ProjectCardMediaPlaceholder } from './ProjectCardMediaPlaceholder';
import { ProjectCardRenderedVideoPreview } from './ProjectCardRenderedVideoPreview';
import { ProjectCardRenderStatus } from './ProjectCardRenderStatus';
import { ProjectCardThumbnail } from './ProjectCardThumbnail';

export interface ProjectCardMediaAreaProps {
  thumbnailSrc: string | null;
  renderJob: webApi.EnrichedVideoEditorProjectListItemDto['renderJob'];
  publishedPlatforms: webApi.EnrichedVideoEditorProjectListItemDto['publishedPlatforms'];
  /** When set (generated project with playback URL), thumbnail area gets hover play → inline player. */
  renderedPlaybackUrl: string | null;
  /** When there is no playback preview, hover shows edit (pencil) like the card Edit action. */
  editorUrl: string;
  /** Subtle blueprint-style grid on the media until render is completed. */
  showBlueprintGrid: boolean;
}

/**
 * Top of the card: thumbnail or empty placeholder; top-right shows either upload platform
 * statuses (when there is a completed/failed publish to show) or render job / “Sin generar”.
 */
export function ProjectCardMediaArea({
  thumbnailSrc,
  renderJob,
  publishedPlatforms,
  renderedPlaybackUrl,
  editorUrl,
  showBlueprintGrid,
}: ProjectCardMediaAreaProps) {
  const platformItems = getDisplayPlatformItems({
    uploadStatuses: [],
    publishedPlatforms,
  });

  const hasPublishedPlatformStatuses = platformItems.length > 0;

  const baseMedia = thumbnailSrc ? (
    <ProjectCardThumbnail src={thumbnailSrc} />
  ) : (
    <ProjectCardMediaPlaceholder />
  );

  const media = showBlueprintGrid ? (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {baseMedia}
      <ProjectCardMediaBlueprintGrid />
    </Box>
  ) : (
    baseMedia
  );

  return (
    <Box sx={{ position: 'relative', flexShrink: 0 }}>
      <Box sx={{ position: 'relative', zIndex: 0, flexShrink: 0 }}>
        {renderedPlaybackUrl ? (
          <ProjectCardRenderedVideoPreview playbackUrl={renderedPlaybackUrl}>
            {media}
          </ProjectCardRenderedVideoPreview>
        ) : (
          <ProjectCardMediaEditorHoverOverlay editorUrl={editorUrl}>
            {media}
          </ProjectCardMediaEditorHoverOverlay>
        )}
      </Box>
      <Box
        sx={{
          position: 'absolute',
          zIndex: 3,
          top: 8,
          right: 8,
          maxWidth: 'calc(100% - 16px)',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        {hasPublishedPlatformStatuses ? (
          <CardMediaPill>
            <UploadedPlatformsStatus variant="pill" size="medium" items={platformItems} />
          </CardMediaPill>
        ) : (
          <ProjectCardRenderStatus renderJob={renderJob} />
        )}
      </Box>
    </Box>
  );
}
